import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type IconVariant =
  | "hero"
  | "info"
  | "success"
  | "warning"
  | "chart2"
  | "chart3"
  | "chart4"
  | "destructive";

/* Light vivid in light mode, dark vivid in dark mode. No gray, no repeats. */
const CARD_BG_MAP: Record<IconVariant, string> = {
  hero: "bg-[color-mix(in_oklch,var(--hero-from)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--hero-from)_55%,var(--background))]",
  info: "bg-[color-mix(in_oklch,var(--info)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--info)_55%,var(--background))]",
  success:
    "bg-[color-mix(in_oklch,var(--success)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--success)_55%,var(--background))]",
  warning:
    "bg-[color-mix(in_oklch,var(--warning)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--warning)_55%,var(--background))]",
  chart2:
    "bg-[color-mix(in_oklch,var(--chart-2)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--chart-2)_55%,var(--background))]",
  chart3:
    "bg-[color-mix(in_oklch,var(--chart-3)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--chart-3)_55%,var(--background))]",
  chart4:
    "bg-[color-mix(in_oklch,var(--chart-4)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--chart-4)_55%,var(--background))]",
  destructive:
    "bg-[color-mix(in_oklch,var(--destructive)_32%,var(--background))] dark:bg-[color-mix(in_oklch,var(--destructive)_55%,var(--background))]",
};

const ICON_CONTAINER_MAP: Record<IconVariant, string> = {
  hero: "bg-card border border-border text-hero-from",
  info: "bg-card border border-border text-info",
  success: "bg-card border border-border text-success",
  warning: "bg-card border border-border text-warning",
  chart2: "bg-card border border-border text-[var(--chart-2)]",
  chart3: "bg-card border border-border text-[var(--chart-3)]",
  chart4: "bg-card border border-border text-[var(--chart-4)]",
  destructive: "bg-card border border-border text-destructive",
};

interface CategoryCardProps {
  href: string;
  iconVariant: IconVariant;
  icon: ReactNode;
  title: string;
  description: string;
  ctaText: string;
  className?: string;
}

/** Category card for CustomDressCategoriesSection with icon, and CTA. */
export function CategoryCard({
  href,
  iconVariant,
  icon,
  title,
  description,
  ctaText,
  className = "",
}: CategoryCardProps) {
  const cardBgClass = CARD_BG_MAP[iconVariant];
  const iconContainerClass = ICON_CONTAINER_MAP[iconVariant];

  return (
    <Link href={href} className={`group block ${className}`}>
      <Card
        variant="interactive"
        padding="none"
        className={`h-full group-hover:-translate-y-1 ${cardBgClass}`}
      >
        <CardHeader className="flex flex-col items-center text-center py-6 px-6 pb-2">
          <div
            className={`inline-flex items-center justify-center w-14 h-14 rounded-full shrink-0 mb-4 ${iconContainerClass}`}
            aria-hidden="true"
          >
            {icon}
          </div>
          <CardTitle className="text-xl font-bold">{title}</CardTitle>
        </CardHeader>
        <CardContent className="px-6 pt-0 pb-4 text-center">
          <p className="text-sm text-muted-foreground dark:text-foreground/90">
            {description}
          </p>
        </CardContent>
        <CardFooter className="flex justify-center pt-0 pb-6">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-hero-from group-hover:text-hero-via transition-colors">
            {ctaText}
            <ChevronRight className="w-4 h-4" aria-hidden />
          </span>
        </CardFooter>
      </Card>
    </Link>
  );
}
